import React from 'react';
import { Typography, Divider, Anchor, Alert } from 'antd';
import { Helmet } from 'react-helmet-async';
import { InfoCircleOutlined } from '@ant-design/icons';
import { theme } from '../../styles/theme';
import { PRIVACY_CONTENT } from '../../constants/privacyContent';

const { Title, Paragraph, Text } = Typography;
const { Link } = Anchor;

const PrivacyPolicyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - HSP | HammerSawPaint</title>
        <meta name="description" content="Privacy Policy for HammerSawPaint. Learn how we collect, use, and protect your personal information. CCPA/CPRA compliant." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div style={{ minHeight: '100vh', marginTop: '80px', background: theme.colors.white }}>
        {/* Hero Section */}
        <section style={{ 
          background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
          padding: '60px 20px',
          color: 'white'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <Title level={1} style={{ 
              color: 'white', 
              marginBottom: '1rem',
              fontFamily: theme.fonts.primary,
              fontSize: '2.5rem'
            }}>
              {PRIVACY_CONTENT.hero.title}
            </Title>
            <Text style={{ 
              color: 'white', 
              fontSize: '1.1rem',
              fontFamily: theme.fonts.primary
            }}>
              {PRIVACY_CONTENT.hero.subtitle}
            </Text>
            <br />
            <Text style={{ 
              color: 'white', 
              fontSize: '1rem',
              fontFamily: theme.fonts.primary,
              opacity: 0.9
            }}>
              {PRIVACY_CONTENT.hero.lastUpdated}
            </Text>
          </div>
        </section>

        {/* Content Section */}
        <section style={{ padding: '60px 20px' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            
            {/* Key Highlight */}
            <Alert
              message="Important: Data Sharing for Service Delivery"
              description="To provide home maintenance services, we share your contact and property information with trusted, vetted independent service providers. This sharing is necessary to book and perform the services you request. Service providers may only use your information for service delivery and are prohibited from other uses."
              type="info"
              icon={<InfoCircleOutlined />}
              showIcon
              style={{
                marginBottom: '3rem',
                fontFamily: theme.fonts.primary
              }}
            />

            {/* Introduction */}
            <div style={{ marginBottom: '3rem' }}>
              <Title level={2} style={{ 
                fontFamily: theme.fonts.primary,
                color: theme.colors.primary
              }}>
                {PRIVACY_CONTENT.introduction.title}
              </Title>
              {PRIVACY_CONTENT.introduction.content.map((paragraph, index) => (
                <Paragraph key={index} style={{ 
                  fontSize: '1rem',
                  lineHeight: '1.8',
                  fontFamily: theme.fonts.primary,
                  marginBottom: '1rem'
                }}>
                  {paragraph}
                </Paragraph>
              ))}
            </div>

            <Divider />

            {/* Table of Contents */}
            <div style={{ 
              background: theme.colors.lightGray,
              padding: '2rem',
              borderRadius: theme.borderRadius.lg,
              marginBottom: '3rem'
            }}>
              <Title level={4} style={{ fontFamily: theme.fonts.primary, marginBottom: '1rem' }}>
                Table of Contents
              </Title>
              <Anchor
                affix={false}
                offsetTop={100}
                style={{ fontFamily: theme.fonts.primary }}
              >
                {PRIVACY_CONTENT.sections.map((section, index) => (
                  <Link 
                    key={index}
                    href={`#section-${index}`}
                    title={section.title}
                  />
                ))}
              </Anchor>
            </div>

            {/* Sections */}
            {PRIVACY_CONTENT.sections.map((section, index) => (
              <div key={index} id={`section-${index}`} style={{ marginBottom: '3rem' }}>
                <Title level={2} style={{ 
                  fontFamily: theme.fonts.primary,
                  color: theme.colors.primary,
                  marginBottom: '1.5rem'
                }}>
                  {section.title}
                </Title>
                
                {/* Direct content (for sections without subsections) */}
                {section.content && section.content.map((paragraph, pIndex) => (
                  <Paragraph key={pIndex} style={{ 
                    fontSize: '1rem',
                    lineHeight: '1.8',
                    fontFamily: theme.fonts.primary,
                    marginBottom: '1rem',
                    whiteSpace: 'pre-line'
                  }}>
                    {paragraph}
                  </Paragraph>
                ))}

                {/* Subsections */}
                {section.subsections && section.subsections.map((subsection, subIndex) => (
                  <div key={subIndex} style={{ marginBottom: '2rem', marginLeft: '1rem' }}>
                    {subsection.subtitle && (
                      <Title level={4} style={{ 
                        fontFamily: theme.fonts.primary,
                        color: theme.colors.black,
                        marginBottom: '1rem'
                      }}>
                        {subsection.subtitle}
                      </Title>
                    )}
                    {subsection.content.map((paragraph, pIndex) => (
                      <Paragraph key={pIndex} style={{ 
                        fontSize: '1rem',
                        lineHeight: '1.8',
                        fontFamily: theme.fonts.primary,
                        marginBottom: '0.75rem',
                        whiteSpace: 'pre-line'
                      }}>
                        {paragraph}
                      </Paragraph>
                    ))}
                  </div>
                ))}
                
                {index < PRIVACY_CONTENT.sections.length - 1 && (
                  <Divider style={{ margin: '2rem 0' }} />
                )}
              </div>
            ))}

            {/* Footer */}
            <div style={{
              background: theme.colors.lightGray,
              padding: '2rem',
              borderRadius: theme.borderRadius.lg,
              marginTop: '3rem',
              borderLeft: `4px solid ${theme.colors.primary}`
            }}>
              <Paragraph style={{ 
                fontFamily: theme.fonts.primary,
                marginBottom: '1rem',
                fontWeight: '500'
              }}>
                {PRIVACY_CONTENT.footer.acknowledgment}
              </Paragraph>
              <Paragraph style={{ 
                fontFamily: theme.fonts.primary,
                marginBottom: 0
              }}>
                {PRIVACY_CONTENT.footer.commitment}
              </Paragraph>
            </div>

          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
